import React, { useState } from 'react';
import { Element } from 'react-scroll';
import { motion } from 'framer-motion';
import { FiChevronDown } from 'react-icons/fi';
import { fadeInUp, staggerContainer, cardVariant } from '../../components/functions/animation';
import './FAQ.css';

/* Single source of truth for the visible FAQ. The JSON-LD FAQPage in index.html
   mirrors these Q&As so structured data matches on-page content (AEO/GEO). */
const FAQS = [
  {
    q: 'What are your primary areas of expertise?',
    a: (
      <>
        I&apos;m a Full-Stack and AI/ML Engineer focused on scalable full-stack
        architectures, high-throughput backend services, and production machine-learning
        systems. I bridge reactive React/Next.js frontends with optimized Node.js and
        Python (FastAPI) backends, and build LLM orchestration, RAG pipelines, and
        computer-vision workloads on containerized, self-hosted infrastructure.
      </>
    ),
  },
  {
    q: 'What technologies do you work with?',
    a: (
      <ul className="faq-tech">
        <li><strong>Languages:</strong> TypeScript, JavaScript, Python, Rust, SQL, HTML5, CSS3</li>
        <li><strong>Frontend:</strong> React.js, Next.js, SolidJS, responsive UI architecture</li>
        <li><strong>Backend:</strong> Node.js, Express.js, FastAPI, Flask, REST APIs, microservices</li>
        <li><strong>AI / ML:</strong> Machine Learning, LLM orchestration, RAG, prompt engineering, OpenCV</li>
        <li><strong>Data:</strong> PostgreSQL, MySQL, MongoDB</li>
        <li><strong>Infrastructure:</strong> Docker, GitHub Actions CI/CD, QEMU/KVM, Linux, Git</li>
      </ul>
    ),
  },
  {
    q: 'Can you own the full product lifecycle autonomously?',
    a: (
      <>
        Yes. I&apos;ve worked with high autonomy in fast-moving teams — gathering
        requirements directly with clients, prototyping, designing schemas and APIs, and
        shipping through to deployment. At Outbro I delivered and maintained a full MERN
        platform serving 100+ monthly users; at TechStax I built FastAPI/Flask
        microservices and LLM-driven automation shipped through CI/CD.
      </>
    ),
  },
  {
    q: 'What kind of architecture do you design?',
    a: (
      <>
        Pragmatic, reliable systems — from multi-language microservice pipelines to
        self-hosted modular monoliths running heavy AI workloads (e.g., FitWright&apos;s
        multi-stage LLM/RAG document pipeline). I work across REST APIs, real-time
        transport (Socket.io, WebRTC), and low-latency networking, with performance work
        (code splitting, caching, image optimization) for strong Core Web Vitals.
      </>
    ),
  },
  {
    q: 'What are your most notable projects?',
    a: (
      <>
        <strong>FitWright</strong> — a self-hosted modular monolith with a multi-stage AI
        document pipeline (multi-LLM, RAG, ATS-style scoring; Next.js + FastAPI + Docker).
        {' '}<strong>Outbro.net</strong> — an enterprise marketplace and AI job engine
        pairing a Python ML recommendation microservice with a Node.js/Express backend.
        {' '}<strong>SafeStree</strong> — a real-time women-safety platform combining
        OpenCV gesture detection with WebRTC/Socket low-latency alerts and P2P video.
      </>
    ),
  },
  {
    q: 'Where are you located, and do you work remotely?',
    a: (
      <>
        I&apos;m based in Kolkata, India, and I&apos;m fully set up for remote global roles —
        collaborating across distributed teams with containerized, automated workflows.
      </>
    ),
  },
  {
    q: 'Are you available for hire?',
    a: (
      <>
        Yes — I&apos;m open to global remote software engineering roles and freelance
        projects. Reach me at{' '}
        <a href="mailto:obaidzeeshan.official@gmail.com">obaidzeeshan.official@gmail.com</a>,
        or on{' '}
        <a href="https://www.linkedin.com/in/obaidullah-zeeshan/" target="_blank" rel="noopener noreferrer">LinkedIn</a>{' '}
        and{' '}
        <a href="https://github.com/ObaidGits" target="_blank" rel="noopener noreferrer">GitHub</a>.
      </>
    ),
  },
  {
    q: 'What is your educational background?',
    a: (
      <>
        B.Tech in Computer Science &amp; Engineering from Techno International Batanagar
        (MAKAUT), completed in 2026 with a final CGPA of 8.1/10, plus certifications in the
        MERN stack, Data Science, and DevOps.
      </>
    ),
  },
];

const FAQItem = ({ item, isOpen, onToggle, id }) => (
  <motion.div className={`faq-item${isOpen ? ' open' : ''}`} variants={cardVariant}>
    <h3 className="faq-q-wrap">
      <button
        type="button"
        className="faq-question"
        aria-expanded={isOpen}
        aria-controls={`faq-answer-${id}`}
        id={`faq-question-${id}`}
        onClick={onToggle}
      >
        <span>{item.q}</span>
        <FiChevronDown className="faq-chevron" aria-hidden="true" />
      </button>
    </h3>
    {/* Answer stays in the DOM (collapsed via CSS) so crawlers always read it. */}
    <div
      className="faq-answer"
      id={`faq-answer-${id}`}
      role="region"
      aria-labelledby={`faq-question-${id}`}
    >
      <div className="faq-answer-inner">
        <div className="faq-answer-body">{item.a}</div>
      </div>
    </div>
  </motion.div>
);

const FAQ = () => {
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <Element name="faq">
      <section id="faq">
        <div className="container">
          <motion.div variants={fadeInUp} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }}>
            <p className="section-label">Good to Know</p>
            <h2 className="heading">Frequently Asked <span>Questions</span></h2>
          </motion.div>

          <motion.div
            className="faq-list"
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
          >
            {FAQS.map((item, i) => (
              <FAQItem
                key={i}
                id={i}
                item={item}
                isOpen={openIdx === i}
                onToggle={() => setOpenIdx(openIdx === i ? -1 : i)}
              />
            ))}
          </motion.div>
        </div>
      </section>
    </Element>
  );
};

export default FAQ;
